import { useEffect, useRef, useState } from "react";
import { Sparkles, X, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, streamChat } from "@/lib/api";
import { useLang } from "@/lib/i18n";

function Bubble({ m }) {
  const mine = m.role === "user";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div
        data-testid={`chat-msg-${m.role}`}
        className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
          mine ? "rounded-ee-sm bg-[#007A3D] text-white" : "rounded-es-sm bg-slate-100 text-slate-800"
        }`}
      >
        {m.content.replace(/\*\*/g, "").replace(/^#+\s*/gm, "") || <span className="inline-flex gap-1"><i className="dot" /><i className="dot" /><i className="dot" /></span>}
      </div>
    </div>
  );
}

export default function AIChat() {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (open) api.get("/ai/chat/history").then((r) => setMsgs(r.data)).catch(() => {});
  }, [open]);
  useEffect(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), [msgs]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    try {
      await streamChat(msg, lang, (delta) =>
        setMsgs((m) => {
          const c = [...m];
          c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + delta };
          return c;
        })
      );
    } catch (e) {
      if (e?.message === "PLAN_AI") toast.error(t("planAiLocked"), { action: { label: t("upgrade"), onClick: () => (window.location.href = "/settings") } });
      else toast.error(t("aiError"));
      setMsgs((m) => m.filter((x, i) => !(i === m.length - 1 && !x.content)));
    }
    setBusy(false);
  };

  const clear = async () => {
    await api.delete("/ai/chat/history");
    setMsgs([]);
  };

  return (
    <div className="no-print">
      {!open && (
        <button
          data-testid="ai-chat-open-btn"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 end-6 z-40 flex items-center gap-2 rounded-full bg-[#0B1220] py-3 pe-5 ps-4 text-sm font-semibold text-white shadow-xl ring-2 ring-[#D4AF37]/60 transition-transform hover:scale-[1.03]"
        >
          <Sparkles className="h-4 w-4 text-[#D4AF37]" /> {t("aiAssistant")}
        </button>
      )}
      {open && (
        <div data-testid="ai-chat-panel" className="chat-in fixed bottom-4 end-4 z-50 flex h-[min(620px,calc(100vh-2rem))] w-[min(420px,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
          <div className="flex items-center gap-3 bg-[#0B1220] px-5 py-4 text-white">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-[#D4AF37] to-[#A8862A]"><Sparkles className="h-4 w-4" /></div>
            <div className="flex-1">
              <div className="font-display font-bold">{t("chatTitle")}</div>
              <div className="text-[11px] text-white/50">{t("aiPowered")}</div>
            </div>
            <button data-testid="ai-chat-clear-btn" onClick={clear} title={t("clearChat")} className="rounded p-1.5 text-white/60 hover:bg-white/10 hover:text-white"><Trash2 className="h-4 w-4" /></button>
            <button data-testid="ai-chat-close-btn" onClick={() => setOpen(false)} className="rounded p-1.5 text-white/60 hover:bg-white/10 hover:text-white"><X className="h-4 w-4" /></button>
          </div>
          <div className="flex-1 space-y-3 overflow-y-auto p-4">
            {msgs.length === 0 && (
              <div className="space-y-3">
                <p className="text-sm text-slate-500">{t("aiGreeting")}</p>
                {["sug1", "sug2", "sug3"].map((k) => (
                  <button key={k} data-testid={`ai-suggestion-${k}`} onClick={() => send(t(k))} className="block w-full rounded-lg border border-[#D4AF37]/40 bg-[#FFFBEB] px-3 py-2 text-start text-sm text-slate-700 transition-colors hover:bg-[#FDF3D0]">
                    {t(k)}
                  </button>
                ))}
              </div>
            )}
            {msgs.map((m, i) => <Bubble key={m.id || i} m={m} />)}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 border-t border-slate-200 p-3">
            <input data-testid="ai-chat-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder={t("chatPlaceholder")} className="h-10 flex-1 rounded-full border border-slate-300 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#007A3D]/40" />
            <button data-testid="ai-chat-send-btn" disabled={busy} className="flex h-10 w-10 items-center justify-center rounded-full bg-[#007A3D] text-white transition-colors hover:bg-[#005E2E] disabled:opacity-50"><Send className="h-4 w-4 rtl:-scale-x-100" /></button>
          </form>
        </div>
      )}
    </div>
  );
}
